import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

const CustomDialog = forwardRef(
    ({
        title,
        description,
        children,
        onConfirm,
        confirmText = 'Confirm',
        confirmIcon = 'bi-check-lg',
        cancelText = 'Cancel',
        size = 'md',
        headerActions = null,
    }, ref) => {
        const [open, setOpen] = useState(false);

        useImperativeHandle(ref, () => ({
            handleOpen: () => setOpen(true),
            handleClose: () => setOpen(false),
        }));

        useEffect(() => {
            if (!open) return undefined;
            const onKeyDown = (event) => {
                if (event.key === 'Escape') setOpen(false);
            };
            document.addEventListener('keydown', onKeyDown);
            document.body.style.overflow = 'hidden';
            return () => {
                document.removeEventListener('keydown', onKeyDown);
                document.body.style.overflow = '';
            };
        }, [open]);

        if (!open) return null;

        const handleClose = () => setOpen(false);

        return (
            <div
                className="ecx-survey-modal-backdrop"
                role="presentation"
                onClick={handleClose}
            >
                <div
                    className={`ecx-survey-modal ecx-survey-modal--${size}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label={title}
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className="ecx-survey-modal__header">
                        <div className="ecx-survey-modal__heading">
                            <h3 className="ecx-survey-modal__title">{title}</h3>
                            {description ? (
                                <p className="ecx-survey-modal__desc mb-0">{description}</p>
                            ) : null}
                        </div>
                        <div className="ecx-survey-modal__header-actions">
                            {headerActions}
                            <button
                                type="button"
                                className="ecx-survey-modal__close"
                                aria-label="Close"
                                onClick={handleClose}
                            >
                                <i className="bi bi-x-lg" />
                            </button>
                        </div>
                    </div>

                    <div className="ecx-survey-modal__body">{children}</div>

                    <div className="ecx-survey-modal__footer">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary rounded-pill"
                            onClick={handleClose}
                        >
                            <i className="bi bi-x-lg me-1" />
                            {cancelText}
                        </button>
                        {onConfirm ? (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary rounded-pill"
                                onClick={() => {
                                    onConfirm();
                                }}
                            >
                                <i className={`bi ${confirmIcon} me-1`} />
                                {confirmText}
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    }
);

CustomDialog.displayName = 'CustomDialog';

export default CustomDialog;
